import type { QueryClient } from '@tanstack/svelte-query';
import { getTalksGalleryBaseVariables } from '../talksGalleryPageServerLoadGql/talksGalleryPageServerLoadGql';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { TalksGallerySort } from '#lib/websites/talks/constants/sorting.js';
import { talksGalleryCustomAttributes } from '#lib/websites/talks/pages/TalksGalleryPage/utils/getTalksGalleryFilterSections/getTalksGalleryFilterSections.js';
import {
	GetTalksGalleryDocument,
	type GetTalksGalleryQueryVariables,
} from '#lib/websites/talks/queries/__generated__/getTalksGallery.generated.js';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import type { TalksGalleryPageLoadEvent } from '$routes/talks/gallery/types';

export const TALKS_GALLERY_STALE_TIME = 1000 * 60 * 60;

// The GraphQL version, kept while FeatureFlags.RestPages['/talks/gallery'] is off.
// Keeps the GraphQL shape: this data feeds the GraphQL-keyed query cache, and the
// component reads it through this loader's type while the flag is off; deleted
// with the flag (rest-migration/feature-flags.md).
export const talksGalleryPageLoadGql = async ({
	parent,
	data,
	url,
}: TalksGalleryPageLoadEvent) => {
	const parentData = await parent();
	const queryClient: QueryClient = parentData.queryClient;
	const filterSections = data.filterSections;
	const talks = data.talks;
	const searchParams = url.searchParams;

	queryClient.setQueryData(
		getQueryKeys(
			GetTalksGalleryDocument,
			getTalksGalleryBaseVariables(data.currentDate)
		),
		{
			pageParams: [0],
			pages: [talks],
		}
	);

	const filter = getAppliedFilters({
		searchParams,
		filterSections,
		customAttributes: talksGalleryCustomAttributes,
	});

	const sort = getAppliedSort({
		searchParams,
		defaultSort: [TalksGallerySort.Newest],
	});

	const variables: GetTalksGalleryQueryVariables = {
		...getTalksGalleryBaseVariables(data.currentDate),
		sort,
		filter: {
			_and: [
				...getTalksGalleryBaseVariables(data.currentDate).filter._and,
				...filter,
			],
		},
	};

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetTalksGalleryDocument, variables),
		initialPageParam: 0,
		staleTime: TALKS_GALLERY_STALE_TIME,
	});

	return {
		...parentData,
		...data,
		variables,
	};
};
